import cn from 'classnames';
import emoji from 'emoji-dictionary';
import Image from 'next/future/image';
import Link from 'next/link';
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import htmlParser from 'react-markdown/plugins/html-parser';

import markdownStyle from '../../css/markdown.module.css';
import ROUTES from '../../routes';
import styles from '../blog/Post/post.module.css';
import CodeBlock from '../CodeBlock';
import HeadingRenderer from '../HeadingRenderer';
import Share from '../Share';

import Heading from '@components/Heading';
import { loaderProp } from '@utils/random';

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatDate(timestamp) {
  const date = new Date(timestamp);
  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  const day = date.getUTCDate();

  let suffix = 'th';
  if (day === 1 || day === 21 || day === 31) suffix = 'st';
  else if (day === 2 || day === 22) suffix = 'nd';
  else if (day === 3 || day === 23) suffix = 'rd';

  return `${month} ${day}${suffix}, ${year}`;
}

const Post = ({ headingTag = 'h2', headingSize = 'xs', post }) => {
  const parseHtml = htmlParser({
    isValidNode: (node) => node.type !== 'script',
  });

  const emojiSupport = (text) =>
    text.value.replace(/:\w+:/gi, (name) => emoji.getUnicode(name));

  const route = useMemo(() => ROUTES.changelogPost(post.slug), [post.slug]);

  const renderers = {
    code: CodeBlock,
    heading: (props) => <HeadingRenderer {...props} baseHref={route.asPath} />,
    text: emojiSupport,
  };

  return (
    <>
      <div className="-mt-16 md:mt-0"></div>
      <Link href={route.href} as={route.asPath}>
        <a>
          <Heading
            // @ts-expect-error caused by going for JS to TS
            Tag={headingTag}
            id={post.slug}
            // @ts-expect-error caused by going from JS to TS
            size={headingSize}
            className="hover:opacity-75 mb-2 md:mb-3 font-[700]"
          >
            {post.title}
          </Heading>
        </a>
      </Link>
      <div className="text-gray-500 flex items-center font-inter flex-wrap my-2 justify-center text-sm md:text-base font-medium">
        <div>{formatDate(post.publishDate || post.date)}</div>
      </div>
      {post.coverImage && (
        <div
          style={{ maxWidth: '800px' }}
          className="w-full mx-auto my-6 md:my-10"
        >
          <figure
            className={styles.cover_image_container}
            style={{
              backgroundImage: post.coverImageBackground,
              borderRadius: post.coverImageBackground ? 24 : 0,
            }}
          >
            <div
              style={{
                borderRadius: post.coverImageBorderRadius
                  ? post.coverImageBorderRadius
                  : 0,
                padding: post.coverImageBackground ? '3rem' : 0,
                transform: post.coverImageScale
                  ? `transfrom: scale(${post.coverImageScale})`
                  : undefined,
              }}
            >
              <Image
                src={post.coverImage.fields.file.url}
                className={styles.cover_image}
                width="800"
                height="450"
                alt="Post Cover Image"
                loader={loaderProp}
              />
            </div>
          </figure>
        </div>
      )}
      <div className="max-w-2xl w-full">
        <ReactMarkdown
          className={cn(markdownStyle.popsql__markdown, styles.postMarkdown)}
          source={post.body_markdown || post.body}
          escapeHtml={false}
          renderers={renderers}
          astPlugins={[parseHtml]}
        />
        <Share articleName={post.title} feedback={false} />
      </div>
    </>
  );
};

export default Post;
